import { AppType } from '@blocs.common';
import { useTranslation, TranslateFn } from '@blocs.i18n';
import { FiltersNameEnum } from '../types';

const getIntlCommonFilterLabel = (t: TranslateFn) => ({
  [FiltersNameEnum.isNewTalent]: t('common:filtersName.isNewTalent'),
  [FiltersNameEnum.heightRange]: t('common:filtersName.heightRange'),
  [FiltersNameEnum.weightRange]: t('common:filtersName.weightRange'),
  [FiltersNameEnum.hairColor]: t('common:filtersName.hairColor'),
  [FiltersNameEnum.willingToHairColor]: t('common:filtersName.willingToHairColor'),
  [FiltersNameEnum.hairStyle]: t('common:filtersName.hairStyle'),
  [FiltersNameEnum.willingToHairStyle]: t('common:filtersName.willingToHairStyle'),
  [FiltersNameEnum.facialHair]: t('common:filtersName.facialHair'),
  [FiltersNameEnum.willingToFacialHair]: t('common:filtersName.willingToFacialHair'),
  [FiltersNameEnum.eyeColor]: t('common:filtersName.eyeColor'),
  [FiltersNameEnum.tattoos]: {
    [FiltersNameEnum.list]: t('common:filtersName.tattoosList'),
    [FiltersNameEnum.note]: t('common:filtersName.tattoosNote'),
  },
  [FiltersNameEnum.piercings]: {
    [FiltersNameEnum.list]: t('common:filtersName.piercingsList'),
    [FiltersNameEnum.note]: t('common:filtersName.piercingsNote'),
  },
  [FiltersNameEnum.uniqueAttributes]: t('common:filtersName.uniqueAttributes'),
  [FiltersNameEnum.multipleBirth]: t('common:filtersName.multipleBirth'),
  [FiltersNameEnum.pregnancyDueDateRange]: t('common:filtersName.pregnancyDueDateRange'),
  [FiltersNameEnum.rolePreferences]: t('common:filtersName.rolePreferences'),
  [FiltersNameEnum.sizesCategory]: t('common:filtersName.sizesCategory'),
  [FiltersNameEnum.sizesChildrenCategory]: t('common:filtersName.sizesChildrenCategory'),
  [FiltersNameEnum.chest]: t('common:filtersName.chest'),
  [FiltersNameEnum.neck]: t('common:filtersName.neck'),
  [FiltersNameEnum.sleeve]: t('common:filtersName.sleeve'),
  [FiltersNameEnum.coat]: t('common:filtersName.coat'),
  [FiltersNameEnum.waist]: t('common:filtersName.waist'),
  [FiltersNameEnum.inseam]: t('common:filtersName.inseam'),
  [FiltersNameEnum.glove]: t('common:filtersName.glove'),
  [FiltersNameEnum.hat]: t('common:filtersName.hat'),
  [FiltersNameEnum.menTShirt]: t('common:filtersName.menTShirt'),
  [FiltersNameEnum.menShoes]: t('common:filtersName.menShoes'),
  [FiltersNameEnum.menShoeWidth]: t('common:filtersName.menShoeWidth'),
  [FiltersNameEnum.dress]: t('common:filtersName.dress'),
  [FiltersNameEnum.bust]: t('common:filtersName.bust'),
  [FiltersNameEnum.underbust]: t('common:filtersName.underbust'),
  [FiltersNameEnum.cup]: t('common:filtersName.cup'),
  [FiltersNameEnum.hip]: t('common:filtersName.hip'),
  [FiltersNameEnum.womenTShirt]: t('common:filtersName.womenTShirt'),
  [FiltersNameEnum.womenPants]: t('common:filtersName.womenPants'),
  [FiltersNameEnum.womenShoes]: t('common:filtersName.womenShoes'),
  [FiltersNameEnum.womenShoeWidth]: t('common:filtersName.womenShoeWidth'),
  [FiltersNameEnum.childrenShoes]: t('common:filtersName.childrenShoes'),
  [FiltersNameEnum.childrenShoeWidth]: t('common:filtersName.childrenShoeWidth'),
  [FiltersNameEnum.boys]: t('common:filtersName.boys'),
  [FiltersNameEnum.girls]: t('common:filtersName.girls'),
  [FiltersNameEnum.toddlers]: t('common:filtersName.toddlers'),
  [FiltersNameEnum.infants]: t('common:filtersName.infants'),
  [FiltersNameEnum.skills]: t('common:filtersName.skills'),
  [FiltersNameEnum.skillLevels]: t('common:filtersName.skillLevels'),
  [FiltersNameEnum.dogBreed]: t('common:filtersName.dogBreed'),
  [FiltersNameEnum.dogBreedSize]: t('common:filtersName.dogBreedSize'),
  [FiltersNameEnum.otherAnimals]: t('common:filtersName.otherAnimals'),
  [FiltersNameEnum.props]: t('common:filtersName.props'),
  [FiltersNameEnum.sportsEquipment]: t('common:filtersName.sportsEquipment'),
  [FiltersNameEnum.wardrobe]: t('common:filtersName.wardrobe'),
  [FiltersNameEnum.profession]: t('common:filtersName.profession'),
  [FiltersNameEnum.vehicleType]: t('common:filtersName.vehicleType'),
  [FiltersNameEnum.vehicleYearRange]: t('common:filtersName.vehicleYearRange'),
  [FiltersNameEnum.vehicleMake]: t('common:filtersName.vehicleMake'),
  [FiltersNameEnum.vehicleModel]: t('common:filtersName.vehicleModel'),
  [FiltersNameEnum.documents]: t('common:filtersName.documents'),
  [FiltersNameEnum.gender]: t('common:filtersName.genderAppearance'),
  [FiltersNameEnum.autofillByRoleCriteria]: t('common:filtersName.autofillByRoleCriteria'),
  [FiltersNameEnum.stageName]: t('common:filtersName.stageName'),
  [FiltersNameEnum.additionallyIdentifiesAs]: t('common:filtersName.additionallyIdentifiesAs'),
  [FiltersNameEnum.ethnicAppearance]: t('common:filtersName.ethnicAppearance'),
  [FiltersNameEnum.playableAgeRange]: t('common:filtersName.playableAgeRange'),
  [FiltersNameEnum.workingLocation]: t('common:filtersName.workingLocation'),
  [FiltersNameEnum.unionStatus]: t('common:filtersName.unionStatus'),
  [FiltersNameEnum.excludeUnionStatus]: t('common:filtersName.excludeUnionStatus'),
});

const getIntlCastingFilterLabel = (t: TranslateFn) => ({
  ...getIntlCommonFilterLabel(t),
  [FiltersNameEnum.minorsAgeRange]: t('common:filtersName.minorsAgeRange'),
  [FiltersNameEnum.unrepresented]: t('common:filtersName.unrepresented'),
  [FiltersNameEnum.represented]: t('common:filtersName.represented'),
  [FiltersNameEnum.talentType]: t('common:filtersName.talentType'),
  [FiltersNameEnum.representatives]: t('common:filtersName.representatives'),
  [FiltersNameEnum.representativeList]: t('common:filtersName.representativeList'),
});

const getIntlRepsFilterLabel = (t: TranslateFn) => ({
  ...getIntlCommonFilterLabel(t),
  [FiltersNameEnum.profileStatus]: t('common:filtersName.profileStatus'),
  [FiltersNameEnum.realAgeRange]: t('common:filtersName.realAgeRange'),
  [FiltersNameEnum.excludeConflicts]: t('common:filtersName.excludeConflicts'),
  [FiltersNameEnum.excludeBookedOutDates]: t('common:filtersName.excludeBookedOutDates'),
  [FiltersNameEnum.profileListIds]: t('common:filtersName.profileListIds'),
  [FiltersNameEnum.workPermitExpiration]: t('common:filtersName.workPermitExpiration'),
});

export const useFilterLabel = (appType?: AppType) => {
  const { t } = useTranslation();

  switch (appType) {
    case AppType.Casting:
      return getIntlCastingFilterLabel(t);
    case AppType.Reps:
      return getIntlRepsFilterLabel(t);
    default:
      return getIntlCommonFilterLabel(t);
  }
};
